import {
  mergeOntologies,
  ontologyFromEntry,
  OntologyV2,
} from 'features/dynamicOntology'
import { useRolodexEntries } from 'features/rolodex'
import { useMemo } from 'react'
import { RolodexEntryTagsType, RolodexEntryType } from 'types'
import { compareAlphanumeric } from 'utils/stringHelpers'
import { z } from 'zod'

export interface DocumentOntology {
  id: string
  name: string
  ontology: OntologyV2
  tags: RolodexEntryTagsType
}

export const ROLODEX_DOCUMENT_ONTOLOGY_TYPE =
  'document-ontology' as RolodexEntryType

const DOC_ONT_DATA_SCHEMA = z
  .object({ name: z.string().nonempty('Doc ontology name is required') })
  .strict()

export function useDocumentOntologyAdditions(tags: RolodexEntryTagsType): {
  isLoading: boolean
  forceRefresh: () => Promise<void>
  documentOntologies: ReadonlyArray<DocumentOntology>
} {
  const { entries, isLoading, forceRefresh } = useRolodexEntries(
    useMemo(
      () => ({
        links: [ROLODEX_DOCUMENT_ONTOLOGY_TYPE],
        type: RolodexEntryType.ONTOLOGY,
        tags,
      }),
      [tags]
    )
  )

  const { documentOntologyEntries, ontologyEntries } = useMemo(() => {
    return {
      documentOntologyEntries: entries.filter(
        (e) => e.type === ROLODEX_DOCUMENT_ONTOLOGY_TYPE
      ),
      ontologyEntries: entries.filter(
        (e) => e.type === RolodexEntryType.ONTOLOGY
      ),
    }
  }, [entries])

  return useMemo(() => {
    const documentOntologies: ReadonlyArray<DocumentOntology> =
      documentOntologyEntries
        .map((e) => {
          const { name } = DOC_ONT_DATA_SCHEMA.parse(e.data)
          const ontologyEntriesForDocument = ontologyEntries
            .filter((o) => o.tags[ROLODEX_DOCUMENT_ONTOLOGY_TYPE] === e.id)
            .toSorted((a, b) => a.id.localeCompare(b.id))
            .map(ontologyFromEntry)
          const mergedOntology = mergeOntologies(...ontologyEntriesForDocument)

          return { id: e.id, name, ontology: mergedOntology, tags: e.tags }
        })
        .toSorted((a, b) => compareAlphanumeric(a.name, b.name))
    return { isLoading, documentOntologies, forceRefresh }
  }, [documentOntologyEntries, forceRefresh, isLoading, ontologyEntries])
}
